import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Fingerprint, ScanFace, CreditCard, Wifi, WifiOff, Plus, Trash2,
  RefreshCw, Settings, Users, Clock, CheckCircle, AlertCircle,
  Key, Copy, Server, Activity
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BiometricPage = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [recentPunches, setRecentPunches] = useState([]);
  const [unknownPunches, setUnknownPunches] = useState([]);
  
  // Dialog states
  const [addDeviceOpen, setAddDeviceOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [apiKeyDialog, setApiKeyDialog] = useState({ open: false, key: '' });
  
  // Form states
  const [deviceForm, setDeviceForm] = useState({
    device_name: '',
    device_type: 'fingerprint',
    serial_number: '',
    location: '',
    manufacturer: '',
    model: ''
  });
  
  const [enrollForm, setEnrollForm] = useState({
    entity_type: 'staff',
    entity_id: '',
    biometric_id: '',
    biometric_type: 'fingerprint'
  });
  
  const [staffList, setStaffList] = useState([]);
  const [studentList, setStudentList] = useState([]);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([
      fetchDevices(),
      fetchEnrollments(),
      fetchSyncStatus(),
      fetchRecentPunches(),
      fetchUnknownPunches(),
      fetchStaffAndStudents()
    ]);
    setLoading(false);
  };

  const fetchDevices = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/biometric/devices`, { headers: getAuthHeader() });
      setDevices(res.data);
    } catch (error) {
      console.error('Failed to fetch devices');
    }
  };

  const fetchEnrollments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/biometric/enrollments`, { headers: getAuthHeader() });
      setEnrollments(res.data);
    } catch (error) {
      console.error('Failed to fetch enrollments');
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/biometric/sync-status`, { headers: getAuthHeader() });
      setSyncStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch sync status');
    }
  };

  const fetchRecentPunches = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await axios.get(`${API_URL}/api/biometric/punches?date=${today}`, { headers: getAuthHeader() });
      setRecentPunches(res.data);
    } catch (error) {
      console.error('Failed to fetch punches');
    }
  };

  const fetchUnknownPunches = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/biometric/unknown-punches`, { headers: getAuthHeader() });
      setUnknownPunches(res.data);
    } catch (error) {
      console.error('Failed to fetch unknown punches');
    }
  };

  const fetchStaffAndStudents = async () => {
    try {
      const [teachersRes, staffRes, studentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/teachers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/staff`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/students`, { headers: getAuthHeader() })
      ]);
      const teachers = teachersRes.data?.items || teachersRes.data || [];
      const staff = staffRes.data?.items || staffRes.data || [];
      setStaffList([...teachers.map(t => ({ ...t, type: 'teacher' })), ...staff.map(s => ({ ...s, type: 'staff' }))]);
      setStudentList(studentsRes.data?.items || studentsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch staff/students');
    }
  };

  const addDevice = async () => {
    if (!deviceForm.device_name || !deviceForm.serial_number) {
      toast.error('Device name and serial number are required');
      return;
    }
    
    try {
      const res = await axios.post(`${API_URL}/api/biometric/devices`, deviceForm, { headers: getAuthHeader() });
      toast.success('Device registered!');
      setApiKeyDialog({ open: true, key: res.data.api_key });
      setAddDeviceOpen(false);
      setDeviceForm({ device_name: '', device_type: 'fingerprint', serial_number: '', location: '', manufacturer: '', model: '' });
      fetchDevices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add device');
    }
  };

  const deleteDevice = async (deviceId) => {
    if (!confirm('Delete this device?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/biometric/devices/${deviceId}`, { headers: getAuthHeader() });
      toast.success('Device deleted');
      fetchDevices();
    } catch (error) {
      toast.error('Failed to delete device');
    }
  };

  const enrollBiometric = async () => {
    if (!enrollForm.entity_id || !enrollForm.biometric_id) {
      toast.error('Please select person and enter biometric ID');
      return;
    }
    
    try {
      const endpoint = enrollForm.entity_type === 'student' 
        ? `/api/biometric/enroll/student/${enrollForm.entity_id}`
        : `/api/biometric/enroll/staff/${enrollForm.entity_id}`;
      
      await axios.post(`${API_URL}${endpoint}`, null, {
        headers: getAuthHeader(),
        params: {
          biometric_id: enrollForm.biometric_id,
          biometric_type: enrollForm.biometric_type
        }
      });
      
      toast.success('Biometric enrolled successfully!');
      setEnrollOpen(false);
      setEnrollForm({ entity_type: 'staff', entity_id: '', biometric_id: '', biometric_type: 'fingerprint' });
      fetchEnrollments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getDeviceTypeIcon = (type) => {
    switch (type) {
      case 'fingerprint': return <Fingerprint className="h-5 w-5" />;
      case 'face_recognition': return <ScanFace className="h-5 w-5" />;
      case 'rfid': case 'nfc': return <CreditCard className="h-5 w-5" />;
      default: return <Fingerprint className="h-5 w-5" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return <Badge className="bg-green-500">Active</Badge>;
      case 'offline': return <Badge variant="secondary">Offline</Badge>;
      case 'maintenance': return <Badge className="bg-yellow-500">Maintenance</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <DashboardLayout title="Biometric Attendance">
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Devices</CardTitle>
                <Server className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{devices.length}</div>
                <p className="text-xs text-muted-foreground">
                  {devices.filter(d => d.status === 'active').length} active
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Enrollments</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{syncStatus?.total_enrollments || 0}</div>
                <p className="text-xs text-muted-foreground">biometric IDs</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Today's Punches</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{syncStatus?.today_punches || 0}</div>
                <p className="text-xs text-muted-foreground">attendance records</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Unknown</CardTitle>
                <AlertCircle className="h-4 w-4 text-yellow-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{unknownPunches.length}</div>
                <p className="text-xs text-muted-foreground">unrecognized</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="devices" className="space-y-4">
          <TabsList>
            <TabsTrigger value="devices">Devices</TabsTrigger>
            <TabsTrigger value="enrollments">Enrollments</TabsTrigger>
            <TabsTrigger value="punches">Today's Punches</TabsTrigger>
            <TabsTrigger value="unknown">Unknown Punches</TabsTrigger>
          </TabsList>

          {/* Devices Tab */}
          <TabsContent value="devices" className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-muted-foreground">Manage biometric devices</p>
              <Dialog open={addDeviceOpen} onOpenChange={setAddDeviceOpen}>
                <DialogTrigger asChild>
                  <Button><Plus className="h-4 w-4 mr-2" /> Add Device</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Register New Device</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Device Name *</Label>
                        <Input 
                          placeholder="Main Gate Device"
                          value={deviceForm.device_name}
                          onChange={e => setDeviceForm({...deviceForm, device_name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Device Type *</Label>
                        <Select value={deviceForm.device_type} onValueChange={v => setDeviceForm({...deviceForm, device_type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fingerprint">Fingerprint</SelectItem>
                            <SelectItem value="face_recognition">Face Recognition</SelectItem>
                            <SelectItem value="rfid">RFID Card</SelectItem>
                            <SelectItem value="nfc">NFC</SelectItem>
                            <SelectItem value="qr_code">QR Code</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Serial Number *</Label>
                      <Input 
                        placeholder="ZK-ABC123"
                        value={deviceForm.serial_number}
                        onChange={e => setDeviceForm({...deviceForm, serial_number: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Location</Label>
                        <Input 
                          placeholder="Main Gate"
                          value={deviceForm.location}
                          onChange={e => setDeviceForm({...deviceForm, location: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Manufacturer</Label>
                        <Input 
                          placeholder="ZKTeco"
                          value={deviceForm.manufacturer}
                          onChange={e => setDeviceForm({...deviceForm, manufacturer: e.target.value})}
                        />
                      </div>
                    </div>
                    <Button onClick={addDevice} className="w-full">Register Device</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {devices.map(device => (
                <Card key={device.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getDeviceTypeIcon(device.device_type)}
                        <CardTitle className="text-base">{device.device_name}</CardTitle>
                      </div>
                      {getStatusBadge(device.status)}
                    </div>
                    <CardDescription>{device.serial_number}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Location</span>
                        <span>{device.location || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Type</span>
                        <span className="capitalize">{device.device_type?.replace('_', ' ')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Punches</span>
                        <span>{device.total_punches || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Last Sync</span>
                        <span>{device.last_sync ? new Date(device.last_sync).toLocaleString() : 'Never'}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => toast.info('Device settings coming soon')}>
                        <Settings className="h-4 w-4 mr-1" /> Settings
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => deleteDevice(device.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {devices.length === 0 && (
                <div className="col-span-full text-center py-12 text-muted-foreground">
                  <Server className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No devices registered</p>
                  <p className="text-sm">Add your first biometric device</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Enrollments Tab */}
          <TabsContent value="enrollments" className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-muted-foreground">Manage biometric ID enrollments</p>
              <Dialog open={enrollOpen} onOpenChange={setEnrollOpen}>
                <DialogTrigger asChild>
                  <Button><Plus className="h-4 w-4 mr-2" /> Enroll</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Enroll Biometric ID</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Person Type</Label>
                      <Select value={enrollForm.entity_type} onValueChange={v => setEnrollForm({...enrollForm, entity_type: v, entity_id: ''})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="staff">Staff/Teacher</SelectItem>
                          <SelectItem value="student">Student</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Select Person</Label>
                      <Select value={enrollForm.entity_id} onValueChange={v => setEnrollForm({...enrollForm, entity_id: v})}>
                        <SelectTrigger><SelectValue placeholder="Choose person" /></SelectTrigger>
                        <SelectContent>
                          {(enrollForm.entity_type === 'student' ? studentList : staffList).map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name} {p.type && `(${p.type})`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Biometric ID</Label>
                      <Input 
                        placeholder="Enter ID from device"
                        value={enrollForm.biometric_id}
                        onChange={e => setEnrollForm({...enrollForm, biometric_id: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Biometric Type</Label>
                      <Select value={enrollForm.biometric_type} onValueChange={v => setEnrollForm({...enrollForm, biometric_type: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fingerprint">Fingerprint</SelectItem>
                          <SelectItem value="face">Face</SelectItem>
                          <SelectItem value="card">Card</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={enrollBiometric} className="w-full">Enroll</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Biometric ID</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Enrolled At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enrollments.map(e => (
                    <TableRow key={e.id}>
                      <TableCell className="font-medium">{e.entity_name}</TableCell>
                      <TableCell><Badge variant="outline" className="capitalize">{e.entity_type}</Badge></TableCell>
                      <TableCell className="font-mono">{e.biometric_id}</TableCell>
                      <TableCell className="capitalize">{e.biometric_type}</TableCell>
                      <TableCell>{new Date(e.enrolled_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Today's Punches Tab */}
          <TabsContent value="punches" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Today's Attendance Punches</CardTitle>
                <CardDescription>Biometric punches recorded today</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Device</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentPunches.map(p => (
                      <TableRow key={p.id}>
                        <TableCell>{new Date(p.punch_time).toLocaleTimeString()}</TableCell>
                        <TableCell className="font-medium">{p.entity_name}</TableCell>
                        <TableCell><Badge variant="outline" className="capitalize">{p.entity_type}</Badge></TableCell>
                        <TableCell>{p.device_name}</TableCell>
                        <TableCell>
                          {p.processed ? (
                            <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" /> Processed</Badge>
                          ) : (
                            <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {recentPunches.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Activity className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No punches recorded today</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Unknown Punches Tab */}
          <TabsContent value="unknown" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Unknown Punches</CardTitle>
                <CardDescription>Biometric IDs not enrolled in the system</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Biometric ID</TableHead>
                      <TableHead>Device</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {unknownPunches.map(p => (
                      <TableRow key={p.id}>
                        <TableCell>{new Date(p.punch_time).toLocaleString()}</TableCell>
                        <TableCell className="font-mono">{p.biometric_id}</TableCell>
                        <TableCell>{p.device_id}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {unknownPunches.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-2 opacity-50 text-green-500" />
                    <p>No unknown punches!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* API Key Dialog */}
        <Dialog open={apiKeyDialog.open} onOpenChange={(open) => setApiKeyDialog({ ...apiKeyDialog, open })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" /> Device API Key
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Save this API key securely. You will need it to configure the device to send attendance data.
              </p>
              <div className="flex gap-2">
                <Input value={apiKeyDialog.key} readOnly className="font-mono text-sm" />
                <Button variant="outline" onClick={() => copyToClipboard(apiKeyDialog.key)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <div className="p-4 bg-muted rounded-lg text-sm">
                <p className="font-medium mb-2">Device Configuration:</p>
                <p className="text-muted-foreground">
                  API Endpoint: <code className="bg-background px-1 rounded">{API_URL}/api/biometric/punch</code>
                </p>
                <p className="text-muted-foreground">
                  Header: <code className="bg-background px-1 rounded">X-API-Key: {apiKeyDialog.key.substring(0, 8)}...</code>
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default BiometricPage;
